import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Icons } from './Icons';

const EASE = [0.22, 1, 0.36, 1];
const SANS = '"Geist", "Inter", system-ui, sans-serif';

const TYPE_META = {
  score_change:       { icon: Icons.Star,      color: '#818cf8', label: 'NIL Score'   },
  contract_expiry:    { icon: Icons.Clock,     color: '#f59e0b', label: 'Contract'    },
  missing_contract:   { icon: Icons.Warning,   color: '#f59e0b', label: 'Earnings'    },
  intake_draft_ready: { icon: Icons.Ghost,     color: '#7B63FD', label: 'Ghost Agent' },
};

function fmtTime(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)    return 'just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function NotifItem({ n, onRead }) {
  const meta = TYPE_META[n.type] || { icon: Icons.Bell, color: 'rgba(255,255,255,0.28)', label: 'Update' };
  const Icon = meta.icon;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 14 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 14 }}
      transition={{ duration: 0.35, ease: EASE }}
      onClick={() => !n.read && onRead(n.id)}
      style={{
        display: 'flex', gap: 12, padding: '14px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        cursor: n.read ? 'default' : 'pointer',
        background: n.read ? 'transparent' : 'rgba(255,255,255,0.025)',
        transition: 'background 200ms ease',
      }}
    >
      {/* Icon dot */}
      <div style={{
        flexShrink: 0, width: 34, height: 34, borderRadius: 10,
        background: `color-mix(in srgb, ${meta.color} 14%, rgba(0,0,0,0))`,
        border: `1px solid color-mix(in srgb, ${meta.color} 24%, transparent)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon style={{ width: 16, height: 16, color: meta.color }} />
      </div>

      {/* Body */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: 3 }}>
          <span style={{
            fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em',
            textTransform: 'uppercase', color: meta.color,
          }}>
            {meta.label}
          </span>
          <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.25)', flexShrink: 0 }}>
            {fmtTime(n.created_at)}
          </span>
        </div>
        <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: 600, color: 'rgba(255,255,255,0.88)', lineHeight: 1.35, marginBottom: 2 }}>
          {n.title}
        </p>
        <p style={{ margin: 0, fontSize: '0.77rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.4 }}>
          {n.body}
        </p>
      </div>

      {/* Unread dot */}
      {!n.read && (
        <div style={{
          flexShrink: 0, width: 7, height: 7, borderRadius: '50%',
          background: meta.color, marginTop: 4,
          boxShadow: `0 0 6px ${meta.color}88`,
        }} />
      )}
    </motion.div>
  );
}

export default function NotificationDrawer({ open, onClose, notifications, unreadCount, onRead, onReadAll, hasMore, onLoadMore }) {
  const overlayRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (overlayRef.current && e.target === overlayRef.current) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            ref={overlayRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            style={{
              position: 'fixed', inset: 0, zIndex: 1100,
              background: 'rgba(0,0,0,0.48)', backdropFilter: 'blur(2px)',
            }}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.38, ease: EASE }}
            style={{
              position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 1101,
              width: 'min(380px, 92vw)',
              background: '#0F0F17',
              borderLeft: '1px solid rgba(255,255,255,0.07)',
              display: 'flex', flexDirection: 'column',
              fontFamily: SANS,
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '18px 16px 14px',
              borderBottom: '1px solid rgba(255,255,255,0.07)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '1rem', fontWeight: 700, color: 'rgba(255,255,255,0.9)', letterSpacing: '-0.01em' }}>
                  Notifications
                </span>
                {unreadCount > 0 && (
                  <span style={{
                    fontSize: '0.65rem', fontWeight: 700,
                    background: '#7B63FD', color: '#fff',
                    borderRadius: 20, padding: '1px 7px',
                    letterSpacing: '0.02em',
                  }}>
                    {unreadCount}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {unreadCount > 0 && (
                  <button
                    onClick={onReadAll}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)',
                      fontFamily: SANS, fontWeight: 600, padding: '4px 6px',
                      transition: 'color 150ms', letterSpacing: '0.01em',
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.35)'}
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={onClose}
                  style={{
                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 8, cursor: 'pointer', padding: '5px 6px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Icons.Close style={{ width: 14, height: 14, color: 'rgba(255,255,255,0.5)' }} />
                </button>
              </div>
            </div>

            {/* List */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {notifications.length === 0 ? (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  height: 260, gap: 10,
                }}>
                  <Icons.Bell style={{ width: 32, height: 32, color: 'rgba(255,255,255,0.12)' }} />
                  <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.25)', fontWeight: 500 }}>
                    No notifications yet
                  </span>
                </div>
              ) : (
                <>
                  <AnimatePresence>
                    {notifications.map(n => (
                      <NotifItem key={n.id} n={n} onRead={onRead} />
                    ))}
                  </AnimatePresence>
                  {hasMore && (
                    <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                      <button
                        onClick={onLoadMore}
                        style={{
                          width: '100%', padding: '9px', borderRadius: 10,
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.07)',
                          color: 'rgba(255,255,255,0.4)', cursor: 'pointer',
                          fontSize: '0.75rem', fontWeight: 600, fontFamily: SANS,
                          transition: 'background 150ms ease',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                      >
                        Load more
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
